import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowRight,
  Asterisk,
  Compass,
  Gauge,
  LineChart,
  Footprints,
  RefreshCw,
  ShieldCheck,
  Send,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { WeaveLogo, WeaveMark } from "@/components/WeaveLogo";
import { MaskedLine, FadeUp, Overline } from "@/components/Motion";
import { registerInterest } from "@/lib/api";
import { DepositButton } from "@/components/DepositButton";
import { ProspectusDialog } from "@/components/ProspectusDialog";

const HERO_IMG =
  "https://images.unsplash.com/photo-1619459074340-2ad2fbba3014?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHx3b3ZlbiUyMHRocmVhZCUyMGFic3RyYWN0JTIwdGV4dHVyZSUyMGNhbG18ZW58MHx8fHwxNzg2Mzc2MDU5fDA&ixlib=rb-4.1.0&q=85";
const PORTRAIT_IMG =
  "https://images.unsplash.com/photo-1738566061874-2160e9f4a064?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxtYXR1cmUlMjBwcm9mZXNzaW9uYWwlMjBjYWxtJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg2Mzc2MDU5fDA&ixlib=rb-4.1.0&q=85";

const CAPACITIES = [
  { n: "01", name: "Inner Stability", helps: "Remain emotionally grounded and psychologically resilient during uncertainty." },
  { n: "02", name: "Cognitive Capacity", helps: "Think clearly, prioritise, and make decisions under pressure." },
  { n: "03", name: "Physical Capacity", helps: "Sustain energy, manage pressure, and recover effectively." },
  { n: "04", name: "Adaptive Capacity", helps: "Learn, experiment, and adjust to change." },
  { n: "05", name: "Relational Capacity", helps: "Build trust, communicate effectively, and seek support." },
  { n: "06", name: "Purposeful Action", helps: "Turn intention into consistent action." },
  { n: "07", name: "Founder Transition", helps: "Navigate the shift from experienced professional to founder with confidence and curiosity." },
];

const RECEIVE = [
  { icon: Gauge, text: "A personal 1–5 rating across the seven founder capacities." },
  { icon: Compass, text: "Your Founder Capacity Wheel — a visual picture of how your profile balances today." },
  { icon: LineChart, text: "Plain-language insight into the strengths you already bring and the capacities you could develop." },
  { icon: Footprints, text: "Practical next steps, and the Academy experiences most relevant to you." },
  { icon: RefreshCw, text: "A snapshot you can repeat later, to see how far you've come." },
];

const Header = () => (
  <header className="fixed top-0 inset-x-0 z-50 bg-weave-cream/80 backdrop-blur-xl border-b border-weave-line">
    <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
      <Link to="/" aria-label="The Weave home"><WeaveLogo /></Link>
      <Link
        to="/assessment"
        data-testid="header-start-button"
        className="hidden sm:inline-flex items-center gap-2 bg-weave-teal text-white hover:bg-weave-tealdeep transition-colors duration-300 rounded-full px-6 py-2.5 text-sm font-medium"
      >
        Discover your founder capacity <ArrowRight size={16} />
      </Link>
    </div>
  </header>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yMark = useTransform(scrollYProgress, [0, 1], [0, -90]);

  return (
    <section ref={ref} className="relative min-h-[94vh] flex items-center overflow-hidden pt-16" data-testid="hero-section">
      <motion.div style={{ y: yImg }} className="absolute inset-0 -z-10" aria-hidden="true">
        <img src={HERO_IMG} alt="" className="w-full h-[120%] object-cover opacity-[0.12] mix-blend-multiply" />
      </motion.div>

      <motion.div style={{ y: yMark }} className="absolute right-[6%] top-[22%] -z-10 hidden lg:block" aria-hidden="true">
        <div className="relative w-[420px] h-[420px]">
          <div className="absolute inset-0 rounded-full border border-dashed border-weave-teal/30 animate-spin-slow" />
          <div className="absolute inset-10 rounded-full border border-weave-line" />
          <div className="absolute inset-0 flex items-center justify-center float-slow">
            <WeaveMark size={220} className="opacity-90" />
          </div>
        </div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full py-24">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-xs font-bold uppercase tracking-[0.25em] text-weave-teal mb-8"
          data-testid="hero-overline"
        >
          The Weave · Founder Capacity Index
        </motion.p>

        <h1 className="text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter text-weave-ink leading-[1.08] max-w-4xl" data-testid="hero-headline">
          <MaskedLine delay={0.15}>You’ve built a career.</MaskedLine>
          <MaskedLine delay={0.3}><span className="text-weave-teal font-normal">Could you build something of your own?</span></MaskedLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.75 }}
          className="mt-8 max-w-xl"
          data-testid="hero-subcopy"
        >
          <p className="text-lg md:text-xl font-normal text-weave-slate leading-relaxed">
            You may feel ready for a different future, without yet being certain what that future
            looks like — or whether you have what it takes to create it.
          </p>
          <p className="mt-4 text-lg md:text-xl font-normal text-weave-slate leading-relaxed">
            The free Founder Capacity Index helps you recognise the strengths and experience you
            already bring, identify the capacities you could develop, and discover your next step
            towards building a meaningful business.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-10"
        >
          <Link
            to="/assessment"
            data-testid="hero-start-button"
            className="group inline-flex items-center gap-3 bg-weave-teal text-white hover:bg-weave-tealdeep transition-colors duration-300 rounded-full px-8 py-4 font-medium tracking-wide"
          >
            Discover your founder capacity
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <p className="mt-4 text-sm text-weave-mute" data-testid="hero-supporting-line">
            Complete the Index and receive your personalised report.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

const MarqueeStrip = () => (
  <div className="bg-weave-ink text-weave-cream py-6" data-testid="marquee-strip">
    <Marquee speed={28} gradient={false} pauseOnHover>
      {["Founder First; Business Second", "Find clarity", "Test possibility", "Build what matters", "You don't have to build alone"].map(
        (phrase) => (
          <span key={phrase} className="flex items-center gap-12 mx-6 text-2xl font-light tracking-wide uppercase">
            {phrase}
            <Asterisk className="text-weave-yellow" size={28} />
          </span>
        )
      )}
    </Marquee>
  </div>
);

const ChapterOne = () => (
  <section id="how-it-works" className="py-24 md:py-32 lg:py-40" data-testid="chapter-snapshot">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
      <div className="md:col-span-7">
        <FadeUp>
          <Overline>01 — Not a test. A snapshot.</Overline>
          <h2 className="mt-6 text-3xl md:text-4xl font-normal tracking-tight text-weave-ink leading-tight max-w-xl">
            There are no right or wrong answers here — only honest ones.
          </h2>
          <p className="mt-8 text-lg md:text-xl text-weave-slate leading-relaxed max-w-xl">
            Whatever brought you to this page — a niggling feeling, a restructuring, a long-held
            ambition — the Index meets you where you are. You’ll respond to 40 statements about the{" "}
            <strong className="text-weave-ink font-medium">last three months</strong>: not your ideal
            self, and not your whole career.
          </p>
          <p className="mt-6 text-lg md:text-xl text-weave-slate leading-relaxed max-w-xl">
            What you receive back is a picture of the capacities you already draw on, the ones that
            practice could strengthen, and where the Founder Academy might help. Scores can change.
            That is the point.
          </p>
        </FadeUp>
      </div>
      <div className="md:col-span-5">
        <FadeUp delay={0.15} className="bg-white border border-weave-line rounded-2xl p-8 md:p-10">
          <h3 className="text-xl md:text-2xl font-medium text-weave-ink" data-testid="receive-title">What you will receive</h3>
          <ul className="mt-8 space-y-6">
            {RECEIVE.map(({ icon: Icon, text }) => (
              <li key={text} className="flex gap-4 items-start group">
                <span className="mt-0.5 shrink-0 w-9 h-9 rounded-full bg-weave-sand flex items-center justify-center text-weave-teal transition-colors duration-300 group-hover:bg-weave-teal group-hover:text-white">
                  <Icon size={17} />
                </span>
                <span className="text-base text-weave-slate leading-relaxed">{text}</span>
              </li>
            ))}
          </ul>
        </FadeUp>
      </div>
    </div>
  </section>
);

const ChapterTwo = () => (
  <section className="py-24 md:py-32 lg:py-40 bg-weave-sand/60 border-y border-weave-line" data-testid="chapter-capacities">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
      <div className="md:col-span-4">
        <div className="md:sticky md:top-28">
          <FadeUp>
            <Overline>02 — The seven capacities</Overline>
            <h2 className="mt-6 text-3xl md:text-4xl font-normal tracking-tight text-weave-ink leading-tight">
              You are more than a single score.
            </h2>
            <p className="mt-6 text-base text-weave-slate leading-relaxed">
              Readiness for founder life is multidimensional. You might have motivation to spare, yet
              little left in the tank for recovery. You might build trust easily, yet hesitate to
              take the first step. The Index maps all seven capacities — so the pattern, not a
              number, tells your story.
            </p>
            <Link
              to="/assessment"
              data-testid="capacities-start-button"
              className="mt-8 inline-flex items-center gap-2 text-weave-teal font-medium group"
            >
              Start your snapshot
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </div>
      <div className="md:col-span-8">
        <ul className="divide-y divide-weave-line border-t border-b border-weave-line" data-testid="capacity-list">
          {CAPACITIES.map((c, i) => (
            <FadeUp key={c.n} delay={i * 0.05}>
              <li className="group flex gap-6 md:gap-10 items-baseline py-7 md:py-8 px-2 md:px-4 transition-colors duration-300 hover:bg-white">
                <span className="text-sm font-bold text-weave-red tracking-widest">{c.n}</span>
                <div>
                  <h3 className="text-xl md:text-2xl font-medium text-weave-ink transition-colors duration-300 group-hover:text-weave-teal">
                    {c.name}
                  </h3>
                  <p className="mt-2 text-base text-weave-slate leading-relaxed">{c.helps}</p>
                </div>
              </li>
            </FadeUp>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const ChapterThree = () => {
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Please add your email so we can keep you posted.");
      return;
    }
    setBusy(true);
    try {
      await registerInterest({ type: "register_interest", ...form });
      setDone(true);
      toast.success("You're on the list for the April 2027 cohort.");
    } catch {
      toast.error("Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="py-24 md:py-32 lg:py-40 bg-weave-ink text-weave-cream" data-testid="chapter-academy">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-7">
          <FadeUp>
            <Overline dark>03 — From snapshot to practice</Overline>
            <h2 className="mt-6 text-3xl md:text-5xl font-light tracking-tight leading-[1.15]">
              The Index shows you where you stand.{" "}
              <span className="text-weave-yellow font-normal">The Academy helps you move.</span>
            </h2>
            <p className="mt-8 text-lg text-weave-cream/80 leading-relaxed max-w-xl">
              The Weave Founder Academy is a year-long practice ground where those capacities grow
              through doing — Sophrology-informed mindset and leadership work, real-world
              experiments, mentoring, peer learning, and practical business education. Over the
              year, you’ll move from a cloudy sense of what you want to change, to a tested side
              hustle, and the makings of a business that is genuinely yours.
            </p>
            <p className="mt-8 text-xl font-light text-weave-cream tracking-wide">
              Find clarity. Test possibility. <span className="text-weave-teal">Build what matters.</span>
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-6">
              <ProspectusDialog
                triggerText="Download the Personal Clarity prospectus"
                testId="academy-prospectus-button"
                triggerClassName="group inline-flex items-center gap-3 bg-weave-yellow text-weave-ink hover:bg-[#e5ab38] transition-colors duration-300 rounded-full px-7 py-3.5 font-medium"
              />
              <a
                href="https://tidycal.com/the-weave/30-minute-meeting"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="academy-clarity-consult-link"
                className="group inline-flex items-center gap-2 text-weave-yellow font-medium"
              >
                Or book a 30-minute Founder Clarity Consult
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </FadeUp>
        </div>
        <div className="md:col-span-5">
          <FadeUp delay={0.15} className="bg-weave-cream text-weave-ink rounded-2xl p-8 md:p-10">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-weave-red bg-weave-red/10 rounded-full px-4 py-2">
              First cohort · April 2027
            </span>
            <div className="mt-6 rounded-xl border border-weave-line bg-white p-5" data-testid="academy-pricing">
              <p className="text-sm text-weave-slate leading-relaxed">
                <strong className="text-weave-ink font-medium">Not sure whether founding is your next chapter?</strong>{" "}
                You don’t need to decide that today.
              </p>
              <p className="mt-2 text-sm text-weave-slate leading-relaxed">
                Personal Clarity gives you ten structured weeks — including the Founder Capacity Index —
                to explore what you want, understand what you bring, and work out whether building
                something of your own genuinely fits your life.
              </p>
              <p className="mt-2 text-sm text-weave-slate leading-relaxed">
                <span className="text-weave-red font-medium">£499 including VAT.</span> If you decide
                you want to build something, £2,501 secures the remaining Business and External
                Clarity sections, bringing the full journey to £3,000.
              </p>
              <div className="mt-4 flex flex-col items-start gap-3">
                <DepositButton email={form.email} plan="clarity" testId="landing-clarity-button" />
              </div>
            </div>
            <h3 className="mt-6 text-xl md:text-2xl font-medium">Register your interest</h3>
            <p className="mt-3 text-sm text-weave-slate leading-relaxed">
              Leave your details and we will keep you posted as the first cohort takes shape. No
              obligation, no hard sell.
            </p>
            {done ? (
              <div className="mt-8 flex items-center gap-3 text-weave-teal" data-testid="academy-interest-success">
                <CheckCircle2 size={22} />
                <p className="font-medium">Thank you — you’re on the list.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="academy-interest-form">
                <input
                  type="text"
                  placeholder="First name (optional)"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  data-testid="academy-interest-name-input"
                  className="w-full rounded-xl border border-weave-line bg-white px-4 py-3 text-sm text-weave-ink placeholder:text-weave-mute focus:border-weave-teal focus:outline-none transition-colors duration-200"
                />
                <input
                  type="email"
                  placeholder="Email address"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="academy-interest-email-input"
                  className="w-full rounded-xl border border-weave-line bg-white px-4 py-3 text-sm text-weave-ink placeholder:text-weave-mute focus:border-weave-teal focus:outline-none transition-colors duration-200"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="academy-interest-submit-button"
                  className="w-full inline-flex items-center justify-center gap-2 bg-weave-red text-white hover:bg-[#cf352e] transition-colors duration-300 rounded-full px-8 py-4 font-medium disabled:opacity-60"
                >
                  {busy ? "Registering…" : "Keep me posted"} <Send size={16} />
                </button>
              </form>
            )}
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

const ChapterFour = () => (
  <section className="py-24 md:py-32 lg:py-40" data-testid="chapter-trust">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
      <div className="md:col-span-5 order-2 md:order-1">
        <FadeUp className="relative">
          <div className="absolute -inset-4 border border-weave-line rounded-3xl" aria-hidden="true" />
          <img
            src={PORTRAIT_IMG}
            alt="An experienced professional in calm contemplation"
            className="relative rounded-2xl w-full aspect-[4/5] object-cover"
            data-testid="trust-portrait"
          />
          <div className="absolute -bottom-6 -right-4 md:-right-6 bg-white border border-weave-line rounded-2xl px-6 py-5 max-w-[240px]">
            <ShieldCheck className="text-weave-teal mb-2" size={22} />
            <p className="text-sm text-weave-slate leading-snug">
              Your results will never be used to exclude you from the Academy.
            </p>
          </div>
        </FadeUp>
      </div>
      <div className="md:col-span-7 order-1 md:order-2 md:pl-8">
        <FadeUp>
          <Overline>04 — A tool you can trust</Overline>
          <h2 className="mt-6 text-3xl md:text-4xl font-normal tracking-tight text-weave-ink leading-tight max-w-lg">
            Developmental by design. Never a verdict.
          </h2>
          <p className="mt-8 text-lg md:text-xl text-weave-slate leading-relaxed max-w-xl">
            Questions like these can touch on confidence, energy, and wellbeing — so we have been
            deliberate about what this tool is, and what it never will be.
          </p>
          <ul className="mt-10 space-y-6 max-w-xl">
            {[
              "A developmental reflection tool — not a clinical assessment, a psychometric test, or a prediction of business success.",
              "Your results are never used for automatic admission, funding, or exclusion decisions.",
              "You control whether The Weave contacts you about relevant programmes. Marketing consent is separate, and always your choice.",
              "You can request deletion of your data at any time.",
            ].map((item) => (
              <li key={item} className="flex gap-4 items-start">
                <CheckCircle2 size={20} className="mt-1 shrink-0 text-weave-teal" />
                <span className="text-base md:text-lg text-weave-slate leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/assessment"
            data-testid="trust-start-button"
            className="group mt-10 inline-flex items-center gap-3 bg-weave-teal text-white hover:bg-weave-tealdeep transition-colors duration-300 rounded-full px-8 py-4 font-medium tracking-wide"
          >
            Discover your founder capacity
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="bg-weave-ink text-weave-cream border-t border-weave-cream/10" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 flex flex-col md:flex-row md:items-end justify-between gap-10">
      <div>
        <WeaveLogo dark />
        <p className="mt-4 text-sm text-weave-cream/70 max-w-sm leading-relaxed">
          Founder First; Business Second. Always.
        </p>
        <a
          href="https://www.wearetheweave.co.uk"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-website-button"
          className="group mt-6 inline-flex items-center gap-2 border border-weave-cream/30 text-weave-cream hover:bg-weave-cream hover:text-weave-ink transition-colors duration-300 rounded-full px-6 py-3 text-sm font-medium"
        >
          Find out more about The Weave
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      </div>
      <p className="text-xs text-weave-cream/50 max-w-md leading-relaxed">
        The Founder Capacity Index is a developmental self-reflection tool. It does not provide
        clinical, financial, or psychometric advice, and it makes no automated decisions about you.
      </p>
    </div>
    <div className="border-t border-weave-cream/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
        <p className="text-xs text-weave-cream/40 leading-relaxed" data-testid="footer-legal">
          Relocon t/a The Weave Limited · Company Registration Number 12213782 · VAT Registration
          Number 335471503 ·{" "}
          <a href="https://www.wearetheweave.co.uk" target="_blank" rel="noopener noreferrer" className="underline hover:text-weave-cream/70 transition-colors duration-200">
            www.wearetheweave.co.uk
          </a>{" "}
          ·{" "}
          <a href="https://www.wearetheweave.co.uk/privacy-policy" target="_blank" rel="noopener noreferrer" data-testid="footer-privacy-link" className="underline hover:text-weave-cream/70 transition-colors duration-200">
            Privacy Notice
          </a>
        </p>
      </div>
    </div>
  </footer>
);

export default function Landing() {
  return (
    <div data-testid="landing-page">
      <Header />
      <Hero />
      <MarqueeStrip />
      <ChapterOne />
      <ChapterTwo />
      <ChapterThree />
      <ChapterFour />
      <Footer />
    </div>
  );
}
